/**
 * Viewport-based mobile detection for UI layout decisions (which menu to show,
 * button sizes, CSS breakpoints, etc.). Triggers when the browser is resized
 * across the 1024px threshold — yes, this means a desktop user with a narrow
 * window gets the mobile layout.
 *
 * This is separate from the UA-based isMobile() in @inter-ikea-storageone/shared/utils/platform,
 * which detects actual device type via user-agent. That one is still used for
 * capability-based decisions (e.g. hiding print on phones, analytics platform tags,
 * Sentry tags) where device type matters regardless of window size.
 */
import { useSyncExternalStore } from 'react';

const MOBILE_BREAKPOINT = '(max-width: 1024px)';

const mql = window.matchMedia(MOBILE_BREAKPOINT);

const subscribe = (callback: () => void) => {
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
};

const getSnapshot = () => mql.matches;

export const useIsMobile = () => useSyncExternalStore(subscribe, getSnapshot);

export type ViewportPlatform = 'mobile' | 'desktop';

export const useViewportPlatform = (): ViewportPlatform => (useIsMobile() ? 'mobile' : 'desktop');

/** For non-React code — reads current viewport state at call time */
export const isMobileViewport = () => mql.matches;
